// @ts-ignore
import React from "react";
import { Suspense } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import Button from "@atlaskit/button";
import { token } from "@atlaskit/tokens";

import { messages } from "../messages";
import { fontFamily } from "../util";

import { WACAvatar } from "./avatar";
import { LoadingSpinner } from "./spinner";
import { UserDetailsCard } from "./user-details-card";

const DropdownMenu = React.lazy(() =>
    // @ts-ignore
    import("./dropdown-import").then(({ DropdownMenu }) => ({
        default: DropdownMenu
    }))
);

const DropdownItem = React.lazy(() =>
    import("@atlaskit/dropdown-menu").then(({ DropdownItem }) => ({
        default: DropdownItem
    }))
);

const DropdownItemGroup = React.lazy(() =>
    import("@atlaskit/dropdown-menu").then(({ DropdownItemGroup }) => ({
        default: DropdownItemGroup
    }))
);

export interface LoggedInUserDropdownProps {
    userInfo: {
        readonly displayName: string;
        readonly email: string;
        readonly avatar: string;
    };
    readonly onLogOutClick: () => void;
    readonly onManageLicensesClick: () => void;
    readonly onManageProfileClick: () => void;
    readonly onSwitchAccountClick: () => void;
}

export interface LoggedInUserProps extends LoggedInUserDropdownProps {
    readonly isUseNewLoggedInUserDropdown?: boolean;
}

export const LegacyLoggedInUseDropdown = ({
    userInfo,
    onLogOutClick,
    onManageLicensesClick,
    onManageProfileClick,
    onSwitchAccountClick
}: LoggedInUserDropdownProps) => (
    <DropdownMenu
        spacing="compact"
        placement="bottom-end"
        // @ts-ignore
        trigger={({ triggerRef, ...props }) => (
            <Button
                className="avatar-button"
                css={overrideAvatarButtonStyle}
                {...props}
                iconBefore={
                    <WACAvatar
                        name={userInfo.displayName}
                        src={userInfo.avatar}
                        size={"small"}
                        showTooltip={true}
                    />
                }
                ref={triggerRef}
                style={{ backgroundColor: "transparent" }}
            />
        )}
    >
        <div
            css={loggedInUserContainer}
            data-testid="global-nav-user-profile-container-user-loggedin"
        >
            <DropdownItemGroup>
                <DropdownItem isDisabled={true}>
                    <UserDetailsCard
                        name={userInfo.displayName}
                        src={userInfo.avatar}
                        email={userInfo.email}
                        size={"small"}
                    />
                </DropdownItem>
                <DropdownItem onClick={onSwitchAccountClick}>
                    <FormattedMessage {...messages.switchAccount} />
                </DropdownItem>
                <DropdownItem onClick={onManageProfileClick}>
                    <FormattedMessage {...messages.profile} />
                </DropdownItem>
                <DropdownItem onClick={onManageLicensesClick}>
                    <FormattedMessage {...messages.licenses} />
                </DropdownItem>
                <DropdownItem onClick={onLogOutClick}>
                    <FormattedMessage {...messages.logOut} />
                </DropdownItem>
            </DropdownItemGroup>
        </div>
    </DropdownMenu>
);

export const LoggedInUseDropdown = ({
    userInfo,
    onLogOutClick,
    onManageLicensesClick,
    onManageProfileClick,
    onSwitchAccountClick
}: LoggedInUserDropdownProps) => {
    const [isNameLengthExceedLimit, setIsNameLengthExceedLimit] =
        React.useState(false);
    const [displayedName, setDisplayedName] = React.useState(
        () => userInfo.displayName ?? ""
    );

    React.useEffect(() => {
        if (userInfo.displayName.length > 15) {
            setIsNameLengthExceedLimit(true);
            setDisplayedName(userInfo.displayName.slice(0, 15));
        }
    }, [userInfo.displayName]);

    return (
        <DropdownMenu
            placement="bottom-end"
            // @ts-ignore
            trigger={({ triggerRef, ...props }) => (
                <Button
                    className="avatar-button"
                    css={newOverrideAvatarButtonStyle}
                    {...props}
                    iconBefore={
                        <WACAvatar
                            name={userInfo.displayName}
                            src={userInfo.avatar}
                            size={"medium"}
                            showTooltip={true}
                        />
                    }
                    ref={triggerRef}
                    style={{ backgroundColor: "transparent" }}
                >
                    {displayedName}
                    {isNameLengthExceedLimit ? "..." : ""}
                </Button>
            )}
        >
            <div
                css={updatedLoggedInUserContainer}
                data-testid="global-nav-user-profile-container-user-loggedin"
            >
                <DropdownItemGroup css={dropdownListStyle}>
                    <DropdownItem isDisabled={true}>
                        <span css={disabledItemStyle}>{userInfo.email}</span>
                    </DropdownItem>
                    <DropdownItem onClick={onSwitchAccountClick}>
                        <span css={dropdownItemStyle}>
                            <FormattedMessage {...messages.switchAccount} />
                        </span>
                    </DropdownItem>
                    <DropdownItem onClick={onManageProfileClick}>
                        <span css={dropdownItemStyle}>
                            <FormattedMessage {...messages.profile} />
                        </span>
                    </DropdownItem>
                    <DropdownItem onClick={onManageLicensesClick}>
                        <span css={dropdownItemStyle}>
                            <FormattedMessage {...messages.licenses} />
                        </span>
                    </DropdownItem>
                    <DropdownItem onClick={onLogOutClick}>
                        <span css={dropdownItemStyle}>
                            <FormattedMessage {...messages.logOut} />
                        </span>
                    </DropdownItem>
                </DropdownItemGroup>
            </div>
        </DropdownMenu>
    );
};

export const LoggedInUser = ({
    userInfo,
    onLogOutClick,
    onManageLicensesClick,
    onManageProfileClick,
    onSwitchAccountClick,
    isUseNewLoggedInUserDropdown = false
}: LoggedInUserProps) => {
    return (
        <Suspense fallback={LoadingSpinner}>
            <div data-testid="main-container" css={mainContainer}>
                {isUseNewLoggedInUserDropdown ? (
                    <LoggedInUseDropdown
                        userInfo={userInfo}
                        onLogOutClick={onLogOutClick}
                        onManageLicensesClick={onManageLicensesClick}
                        onManageProfileClick={onManageProfileClick}
                        onSwitchAccountClick={onSwitchAccountClick}
                    />
                ) : (
                    <LegacyLoggedInUseDropdown
                        userInfo={userInfo}
                        onLogOutClick={onLogOutClick}
                        onManageLicensesClick={onManageLicensesClick}
                        onManageProfileClick={onManageProfileClick}
                        onSwitchAccountClick={onSwitchAccountClick}
                    />
                )}
            </div>
        </Suspense>
    );
};

const mainContainer = css({
    "fontFamily": fontFamily,
    ":hover, :focus, :active": {
        opacity: 0.8
    }
});
const loggedInUserContainer = css({
    "fontFamily": fontFamily,
    "width": "250px",
    "lineHeight": token("space.150"),
    "div::before": {
        content: ""
    },
    "button[disabled]": {
        padding: token("space.0"),
        cursor: "auto"
    },
    [`button[role="menuitem"]`]: {
        fontFamily: fontFamily,
        fontSize: token("space.150")
    }
});

const updatedLoggedInUserContainer = css({
    "width": "264px",
    "padding": token("space.500"),
    "lineHeight": token("space.200"),

    "button": {
        padding: token("space.0"),
        minHeight: "12px"
    },

    [`button[role="menuitem"]`]: {
        "&:hover": {
            backgroundColor: "transparent"
        }
    },

    "div::before": {
        display: "none"
    },
    "div::after": {
        display: "none"
    }
});

const dropdownListStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200")
});

const disabledItemStyle = css({
    fontSize: "12px",
    fontWeight: "700",
    textTransform: "uppercase",
    color: "#101214",
    lineHeight: "16px",
    fontFamily: "var(--bxp-charlie-text-font-family)",
    display: "block"
});

const dropdownItemStyle = css({
    "fontSize": "14px",
    "lineHeight": "24px",
    "fontFamily": "var(--bxp-charlie-text-font-family)",
    "display": "block",

    "&:hover": {
        color: "#1868DB"
    }
});

const overrideAvatarButtonStyle = css({
    padding: token("space.0"),
    width: token("space.250"),
    height: token("space.250"),
    borderRadius: "50%"
});

const newOverrideAvatarButtonStyle = css({
    padding: "0"
});
